import {
  isArray,
  isBoolean,
  isDeepEqual,
  isPlainObject,
  isString,
  map,
  pipe,
  uniqueBy
} from 'remeda'

// Public types ----------------------------------------------------------------

export type Decoration = 'none' | 'icon' | 'color' | 'badge'

export type ComboboxOption = Readonly<{
  label: string
  value: string
  disabled?: boolean
  icon?: string
  color?: string
}>

export type OptionPageRequest = Readonly<{
  generation: number
  filter: string
  page: number
  pageSize: number
}>

export type OptionPageReply = Readonly<{
  result: readonly ComboboxOption[]
  size: number
}>

export type RemoteLoadState =
  | Readonly<{
      status: 'not-asked'
      generation: number
      options: readonly ComboboxOption[]
    }>
  | Readonly<{
      status: 'loading'
      generation: number
      filter: string
      page: number
      options: readonly ComboboxOption[]
      loadedValues: readonly string[]
      total?: number
    }>
  | Readonly<{
      status: 'ready'
      generation: number
      filter: string
      nextPage: number
      options: readonly ComboboxOption[]
      loadedValues: readonly string[]
      total: number
    }>
  | Readonly<{
      status: 'empty'
      generation: number
      filter: string
      options: readonly ComboboxOption[]
    }>
  | Readonly<{
      status: 'failure'
      generation: number
      filter: string
      options: readonly ComboboxOption[]
      message: string
    }>

// Constants -------------------------------------------------------------------

export const OPTION_PAGE_SIZE = 50

const DECORATIONS: ReadonlySet<string> = new Set([
  'none',
  'icon',
  'color',
  'badge'
])

// Private functions -----------------------------------------------------------

const isOptionalString = (value: unknown): boolean =>
  value === undefined || isString(value)

const isOptionalBoolean = (value: unknown): boolean =>
  value === undefined || isBoolean(value)

const selectedOptions = (
  options: readonly ComboboxOption[],
  values: readonly string[]
): readonly ComboboxOption[] =>
  options.filter((option) => values.includes(option.value))

const uniqueValues = (options: readonly ComboboxOption[]): readonly string[] =>
  pipe(
    options,
    map((option) => option.value),
    uniqueBy((value) => value)
  )

const mergeOptions = (
  current: readonly ComboboxOption[],
  incoming: readonly ComboboxOption[],
  selectedValues: readonly string[],
  replace: boolean
): readonly ComboboxOption[] =>
  pipe(
    [
      ...(replace ? selectedOptions(current, selectedValues) : current),
      ...incoming
    ],
    uniqueBy((option) => option.value)
  )

const sameRequest = (
  state: RemoteLoadState,
  request: OptionPageRequest
): state is Extract<RemoteLoadState, { status: 'loading' }> =>
  state.status === 'loading' &&
  state.generation === request.generation &&
  state.filter === request.filter &&
  state.page === request.page

// Public functions ------------------------------------------------------------

export const isDecoration = (value: unknown): value is Decoration =>
  isString(value) && DECORATIONS.has(value)

export const isComboboxOption = (value: unknown): value is ComboboxOption =>
  isPlainObject(value) &&
  isString(value.label) &&
  isString(value.value) &&
  isOptionalBoolean(value.disabled) &&
  isOptionalString(value.icon) &&
  isOptionalString(value.color)

export const parseComboboxOptions = (
  value: unknown
): readonly ComboboxOption[] | undefined =>
  isArray(value) && value.every(isComboboxOption)
    ? uniqueBy(value, (option) => option.value)
    : undefined

export const parseStringValues = (
  value: unknown
): readonly string[] | undefined =>
  isArray(value) && value.every(isString)
    ? uniqueBy(value, (item) => item)
    : undefined

export const parseOptionPageReply = (
  value: unknown
): OptionPageReply | undefined => {
  if (
    !(
      isPlainObject(value) &&
      isArray(value.result) &&
      value.result.every(isComboboxOption) &&
      Number.isSafeInteger(value.size) &&
      typeof value.size === 'number' &&
      value.size >= 0
    )
  ) {
    return undefined
  }

  return {
    result: uniqueBy(value.result, (option) => option.value),
    size: value.size
  }
}

// Builds the shared option envelope, retaining explicit grid row offsets.
export const buildOptionPagePayload = (
  request: Pick<OptionPageRequest, 'filter' | 'page' | 'pageSize'>,
  params: Readonly<Record<string, unknown>>,
  index = request.page * request.pageSize
): Readonly<Record<string, unknown>> => ({
  filter: request.filter,
  page: request.page,
  pageSize: request.pageSize,
  index,
  params
})

export const createRemoteLoadState = (
  options: readonly ComboboxOption[] = []
): RemoteLoadState => ({
  status: 'not-asked',
  generation: 0,
  options
})

export const startRemoteQuery = (
  state: RemoteLoadState,
  filter: string
): readonly [RemoteLoadState, OptionPageRequest] => {
  const generation = state.generation + 1
  const request = {
    generation,
    filter,
    page: 0,
    pageSize: OPTION_PAGE_SIZE
  } as const

  return [
    {
      status: 'loading',
      generation,
      filter,
      page: 0,
      options: state.options,
      loadedValues: []
    },
    request
  ]
}

export const startNextRemotePage = (
  state: RemoteLoadState
): readonly [RemoteLoadState, OptionPageRequest] | undefined => {
  if (state.status !== 'ready' || state.loadedValues.length >= state.total) {
    return undefined
  }

  const request = {
    generation: state.generation,
    filter: state.filter,
    page: state.nextPage,
    pageSize: OPTION_PAGE_SIZE
  } as const

  return [
    {
      status: 'loading',
      generation: state.generation,
      filter: state.filter,
      page: state.nextPage,
      options: state.options,
      loadedValues: state.loadedValues,
      total: state.total
    },
    request
  ]
}

export const receiveRemotePage = (
  state: RemoteLoadState,
  request: OptionPageRequest,
  reply: OptionPageReply,
  selectedValues: readonly string[],
  failureMessage: string
): RemoteLoadState => {
  if (!sameRequest(state, request)) {
    return state
  }

  const options = mergeOptions(
    state.options,
    reply.result,
    selectedValues,
    request.page === 0
  )
  const loadedValues = uniqueBy(
    [...state.loadedValues, ...uniqueValues(reply.result)],
    (value) => value
  )
  const stalled = reply.result.length === 0 && loadedValues.length < reply.size
  const inconsistentTotal = loadedValues.length > reply.size

  if (stalled || inconsistentTotal) {
    return {
      status: 'failure',
      generation: state.generation,
      filter: state.filter,
      options,
      message: failureMessage
    }
  }

  if (request.page === 0 && reply.size === 0) {
    return {
      status: 'empty',
      generation: state.generation,
      filter: state.filter,
      options
    }
  }

  return {
    status: 'ready',
    generation: state.generation,
    filter: state.filter,
    nextPage: request.page + 1,
    options,
    loadedValues,
    total: reply.size
  }
}

export const failRemoteLoad = (
  state: RemoteLoadState,
  request: OptionPageRequest,
  message: string
): RemoteLoadState =>
  sameRequest(state, request)
    ? {
        status: 'failure',
        generation: state.generation,
        filter: state.filter,
        options: state.options,
        message
      }
    : state

export const replaceRemoteBootstrapOptions = (
  state: RemoteLoadState,
  options: readonly ComboboxOption[]
): RemoteLoadState => ({
  status: 'not-asked',
  generation: state.generation + 1,
  options
})

// Server options the loaded results already contain leave nothing to refresh.
export const remoteOptionsContain = (
  state: RemoteLoadState,
  options: readonly ComboboxOption[]
): boolean =>
  options.every((option) =>
    state.options.some((loaded) => isDeepEqual(loaded, option))
  )

// Applies server bootstrap updates without discarding already loaded results.
export const mergeRemoteBootstrapOptions = (
  state: RemoteLoadState,
  options: readonly ComboboxOption[]
): RemoteLoadState => {
  const incomingByValue = new Map(
    options.map((option) => [option.value, option] as const)
  )
  const currentValues = new Set(state.options.map((option) => option.value))

  return replaceRemoteBootstrapOptions(state, [
    ...state.options.map(
      (option) => incomingByValue.get(option.value) ?? option
    ),
    ...options.filter((option) => !currentValues.has(option.value))
  ])
}

export const addRemoteOption = (
  state: RemoteLoadState,
  option: ComboboxOption
): RemoteLoadState => ({
  ...state,
  options: mergeOptions(state.options, [option], [], false)
})

export const remoteOptions = (
  state: RemoteLoadState
): readonly ComboboxOption[] => state.options

// Reports whether retained options belong to the current remote filter.
export const remoteOptionsAreCurrent = (
  state: RemoteLoadState,
  filter: string
): boolean => {
  switch (state.status) {
    case 'ready':
    case 'failure':
      return state.filter === filter
    case 'loading':
      return state.page > 0 && state.filter === filter
    case 'empty':
    case 'not-asked':
      return false
  }
}

export const requestIsCurrent = (
  state: RemoteLoadState,
  request: OptionPageRequest
): boolean => sameRequest(state, request)
